import React from 'react';

import { getWithSelector, groupBySelector } from '@lumapps/lumx/core/utils/selectors';
import { isComponentType } from '@lumapps/utils/types/isComponentType';

import { Combobox } from '../components/Combobox';
import type { BaseSelectProps } from '../types';

/** Render options & sections */
export function renderSelectOptions<O>(props: BaseSelectProps<O>) {
    // Render sections
    if (props.getSectionId && props.options) {
        const { options, getSectionId: optionSectionSelector, renderSectionTitle, ...forwardedProps } = props;
        // Group options by sections
        const sections = groupBySelector(options, optionSectionSelector);

        return Array.from(sections.entries()).map(([title, options], index) => {
            const option = options[0];

            return (
                <Combobox.Section key={title} title={renderSectionTitle ? renderSectionTitle(option, index) : title}>
                    {renderSelectOptions({ options, ...forwardedProps })}
                </Combobox.Section>
            );
        });
    }

    const {
        options,
        getOptionId: optionIdSelector,
        getOptionName: optionNameSelector,
        getOptionDescription: optionDescriptionSelector,
        renderOption,
    } = props;

    // Render options
    return options?.map((item, index) => {
        const id = getWithSelector(optionIdSelector || optionNameSelector, item);
        const name = getWithSelector(optionNameSelector || optionIdSelector, item);
        const description = optionDescriptionSelector && getWithSelector(optionDescriptionSelector, item);

        // Custom option render
        const customOption = renderOption?.(item, index);
        const customOptionProps = isComponentType(Combobox.Option, customOption) ? customOption.props : undefined;
        if (renderOption && !customOptionProps) {
            // Invalid custom render => render nothing
            return null;
        }

        // Use given custom option (if any) and override core props (id, name & description)
        const { children, ...props } = customOptionProps || {};
        return (
            <Combobox.Option {...props} key={id} id={id} textValue={name} description={description}>
                {children || name}
            </Combobox.Option>
        );
    });
}
